import React, { useRef, useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import { AlertMessage } from './ui/AlertMessage';
import { UploadProgressBar } from './ui/UploadProgressBar';
import { Spinner } from './ui/Spinner';
import { useBranding } from '../branding/BrandingContext';
import { usePermissions } from '../permissions/PermissionContext';
import { endpoints } from '../api/endpoints';
import { uploadWithProgress } from '../api/uploadWithProgress';

function Settings() {
  const { companyName, logoUrl, logoKey, loading, refreshBranding } = useBranding();
  const { ownerAdmin } = usePermissions();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const onLogoSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');
    setSuccess('');

    try {
      await uploadWithProgress({
        url: endpoints.branding.uploadLogo,
        file,
        onProgress: (value) => setUploadProgress(value ?? null),
      });
      await refreshBranding();
      setSuccess('Company logo uploaded. It will appear in the sidebar, header, and PDFs.');
    } catch (err) {
      setError(err?.message || 'Unable to upload company logo');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  return (
    <div className="animate-fade-in-up">
      <PageHeader
        title="Settings"
        description="Company branding and application preferences"
      />

      <AlertMessage message={error} type="error" onClose={() => setError('')} />
      <AlertMessage message={success} type="success" onClose={() => setSuccess('')} />

      <section className="rounded-2xl border border-slate-200/80 bg-white shadow-sm p-5 sm:p-6 max-w-xl">
        <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Company branding</h2>
        <p className="text-xs text-slate-500 mt-1">
          Logo is used in the app navigation and on PDF documents.
        </p>

        <dl className="mt-5 space-y-4">
          <div>
            <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide">Company name</dt>
            <dd className="mt-1 text-base font-semibold text-slate-900">
              {loading ? '…' : companyName}
            </dd>
            <p className="text-xs text-slate-500 mt-1">
              Configured on the server (`APP_COMPANY_NAME`).
            </p>
          </div>

          <div>
            <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide">Company logo</dt>
            <dd className="mt-2 flex items-start gap-4">
              <div className="w-20 h-20 rounded-xl border border-slate-200 bg-white p-2 flex items-center justify-center shrink-0">
                {loading ? (
                  <Spinner size="sm" />
                ) : logoUrl ? (
                  <img src={logoUrl} alt={companyName} className="max-w-full max-h-full object-contain" />
                ) : (
                  <span className="text-2xl font-bold text-primary-700">{companyName.charAt(0)}</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                {logoKey ? (
                  <p className="text-xs text-slate-500 break-all">{logoKey}</p>
                ) : (
                  <p className="text-xs text-amber-700">Logo key is not configured on the server.</p>
                )}

                {ownerAdmin ? (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      disabled={uploading || !logoKey}
                      onChange={onLogoSelect}
                    />
                    <button
                      type="button"
                      disabled={uploading || !logoKey}
                      onClick={() => fileInputRef.current?.click()}
                      className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm font-medium text-slate-800 hover:bg-slate-100 disabled:opacity-60"
                    >
                      {uploading ? <Spinner size="sm" /> : null}
                      {uploading ? 'Uploading logo…' : 'Upload new logo'}
                    </button>
                    <p className="text-xs text-slate-500 mt-1">
                      JPEG, PNG or WebP. Max 15 MB. Large files may take a moment — progress is shown below.
                    </p>
                    <UploadProgressBar
                      progress={uploadProgress}
                      indeterminate={uploading && uploadProgress == null}
                      label="Uploading company logo…"
                    />
                  </>
                ) : (
                  <p className="text-xs text-slate-500 mt-2">
                    Only an owner admin can upload a new logo.
                  </p>
                )}
              </div>
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
}

export default Settings;
